import app from '@src/helpers/app';
import {IChatChannelModel} from '@src/@interfaces/models/Chat/IChatChannelModel';
import {IReduxChatsState, IReduxChatsStateChat} from '@src/@interfaces';
import {shortCode} from '@src/helpers/hash/shortCode';
import {parseDate} from '@src/helpers/dates';

export const MESSAGES_PER_PAGE = 30;

const getUserPreference = (key: string, channelId: string | number | null | undefined, defaultValue: any) => {
    const user = app('auth').getUser();
    if (!user || !channelId) {
        return defaultValue;
    }

    return user.getPreference(`chat@channel:${channelId}/${key}`) ?? defaultValue;
};

export function getLastPageNumber(totalMessages: number, perPage: number = MESSAGES_PER_PAGE): number {
    return Math.max(1, Math.ceil(totalMessages / perPage));
}

export function thisErrorIsExpired(error: IReduxChatsState['error'], returnMillisecondsToRelease: false): boolean;
export function thisErrorIsExpired(error: IReduxChatsState['error'], returnMillisecondsToRelease: true): number;
export function thisErrorIsExpired(error: IReduxChatsState['error'], returnMillisecondsToRelease = false): boolean | number {
    const occurredAt = error?.occurredAt;
    if (occurredAt == null) {
        return returnMillisecondsToRelease ? -1 : true;
    }

    const releaseAt = parseDate(occurredAt).add(1, 'minute');
    if (returnMillisecondsToRelease) {
        return releaseAt.diff(new Date(), 'milliseconds');
    }

    return releaseAt.isSameOrBefore();
}

export function getChatUUID(channelId: string | number): string;
export function getChatUUID(entity: NonNullable<IReduxChatsStateChat['entity']>): string;
export function getChatUUID(channelIdOrEntity: NonNullable<string | number | IReduxChatsStateChat['entity']>): string {
    if (typeof channelIdOrEntity === 'object') {
        return shortCode(`${channelIdOrEntity.commentable_type}|${channelIdOrEntity.commentable_id}`);
    }

    return shortCode(`App\\Models\\ChatChannel|${channelIdOrEntity}`);
}

export function pinChatChannel(channel: IChatChannelModel, pinned: boolean) {
    const user = app('auth').getUser();
    if (!user) {
        return Promise.reject(new Error('Exceptions.services.http.unauthorized'));
    }

    return user?.savePreference(`chat@channel:${channel.id}/pinned`, pinned);
}

export function setChatChannelOrderNumber(channel: IChatChannelModel, orderNumber: number) {
    const user = app('auth').getUser();
    if (!user) {
        return Promise.reject(new Error('Exceptions.services.http.unauthorized'));
    }

    return user?.savePreference(`chat@channel:${channel.id}/order-number`, orderNumber);
}

export function enrichChatChannel(channel: IChatChannelModel, orderNumber: number): IReduxChatsState['channels'][number] {
    const userId = app('auth').getUser()?.id;
    const member = channel.members?.find(member => member.user_id === userId);
    const lastViewedAt = member?.last_viewed_at ? +parseDate(member?.last_viewed_at) : 0;

    return {
        ...channel,
        chatUUID: getChatUUID(channel.id as number),
        pinned: getUserPreference('pinned', channel.id, false),
        order_number: getUserPreference('order-number', channel.id, orderNumber),
        last_viewed_at: lastViewedAt,
        last_viewed_page: getLastPageNumber(
            channel.last_read_comment_ordinal_number ?? 0,
            MESSAGES_PER_PAGE
        ),
    };
}
